'use client';

import { useCallback } from 'react';
import { isInternalStoryTraceLabel } from '@terra/ui-design-system';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useGlobalContextMenu } from '../../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../../store/globalContextMenuStore';
import { generateExecutionTabTitle } from '../../../utils/tabTitleUtils';

const getExecutionTabData = (trace: TracePreviewDto) => {
  const objective =
    trace.signal?.description?.trim() ||
    (isInternalStoryTraceLabel(trace.name) ? undefined : trace.name?.trim());

  return {
    id: `executions-${trace.threadId}`,
    title: generateExecutionTabTitle({
      executionTitle: objective || undefined,
      fallback: 'Execution',
    }),
    type: 'executions' as const,
    data: {
      traceId: trace.id,
      threadId: trace.threadId,
      agentName: trace.name,
      ...(trace.executionId && { executionId: trace.executionId }),
    },
  };
};

export function useExecutionsNavigation() {
  const { navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const openTrace = useCallback(
    (trace: TracePreviewDto) => navigateInActiveTab(getExecutionTabData(trace)),
    [navigateInActiveTab],
  );
  const openTraceContextMenu = useCallback(
    ({ event, trace }: { event: MouseEvent; trace: TracePreviewDto }) =>
      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: getExecutionTabData(trace),
          },
        ],
      }),
    [openContextMenu],
  );

  return { openTrace, openTraceContextMenu };
}
